import { getCookie } from '@api/HelperAPI';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { fetchGetFormalOffersLatestOfferConfiguration } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../iwQueryClient/useStateKey';

export const OFFER_FLOW_MEMORY_COOKIE = 'offerFlowMemory';

const STEP_ROUTES: Record<string, string> = {
    funding_requirements: '/funding-requirements/',
    drawdown: '/confirm-funding/'
};

export const useOfferFlowMemoryState = () => {
    const stateKey = useStateKey();
    const isEnabled =
        getCountry() === 'DE' || getCookie(OFFER_FLOW_MEMORY_COOKIE) === 'true';

    const { data: latestOfferConfiguration } = useQuery({
        queryKey: ['fetchGetFormalOffersLatestOfferConfiguration', stateKey],
        queryFn: () =>
            fetchGetFormalOffersLatestOfferConfiguration({ stateKey }),
        select: (data) => data.data.latest_offer_configuration
    });

    const step = latestOfferConfiguration?.step;
    const offerId = latestOfferConfiguration?.offer_id;
    const amount = latestOfferConfiguration?.cash_amount;
    const sweep = latestOfferConfiguration?.revenue_sweep_fraction;

    const hasSavedProgress =
        step === 'funding_requirements' || step === 'drawdown';

    const restoreRoute = step ? STEP_ROUTES[step] : undefined;

    const restoreSearch =
        hasSavedProgress && offerId && amount !== undefined
            ? sweep !== undefined
                ? `?offerId=${offerId}&amount=${amount}&sweep=${sweep}`
                : `?offerId=${offerId}&amount=${amount}`
            : undefined;

    const getRestoreLocation = ({
        fallbackPathname,
        returnPath
    }: {
        fallbackPathname: string;
        returnPath?: string;
    }) => {
        if (hasSavedProgress && isEnabled && restoreRoute) {
            const search =
                returnPath && restoreSearch
                    ? `${restoreSearch}&returnPath=${returnPath}`
                    : restoreSearch;
            return { pathname: restoreRoute, search };
        }
        return { pathname: fallbackPathname };
    };

    return {
        hasSavedProgress,
        step,
        restoreRoute,
        restoreSearch,
        offerId,
        amount,
        sweep,
        isEnabled,
        getRestoreLocation
    };
};
